import { useLiveQuery } from 'dexie-react-hooks';
import { Link, useNavigate } from 'react-router';
import { FotoDaMidia } from '../components/FotoDaMidia.js';
import {
  IconeAlerta,
  IconeDocumento,
  IconeLista,
  IconeRelogio,
  IconeSelo,
  IconeSincronia,
} from '../components/Icones.js';
import { ChipsDeVisoes } from '../components/VisoesRapidas.js';
import {
  Aguardar,
  BotaoLink,
  CabecalhoPagina,
  CartaoGrafico,
  EstadoVazio,
  FaixaIndicadores,
  Indicador,
  Painel,
  PainelAvisos,
  Rosca,
  type Aviso,
} from '../components/ui/index.js';
import { formatarData, hojeIso, idadeDoAnimal } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, Avaliacao, Haras, Midia, Visita } from '../dados/fonte.js';
import { montarLinhas, resumirPlantel, ROTULO_SITUACAO } from '../dados/plantel.js';
import { arquivos, db } from '../lib/local.js';
import { proximaFicha } from './Coleta.js';
import { situacaoDaVisita } from './Visitas.js';

const fechada = (a: Avaliacao) => a.status === 'fechada' || a.status === 'validada';

/**
 * Inicio: o que fazer agora. A equipe ve a visita da vez, as fichas abertas e o
 * que ainda nao subiu; o dono do haras ve o plantel dele e os laudos recentes.
 */
export function Inicio() {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const consulta = useConsulta(async (f) => {
    const [usuario, haras, animais, avaliacoes, visitas, capas] = await Promise.all([
      f.usuarioAtual(),
      f.haras(),
      f.listarAnimais(),
      f.avaliacoes(),
      f.listarVisitas(),
      f.capas(),
    ]);
    return { usuario, haras, animais, avaliacoes, visitas, capas };
  }, []);

  return (
    <Aguardar consulta={consulta}>
      {({ usuario, haras, animais, avaliacoes, visitas, capas }) => {
        const primeiroNome = usuario?.nome?.split(' ')[0];
        const saudacao = `${new Date().getHours() < 12 ? 'Bom dia' : new Date().getHours() < 18 ? 'Boa tarde' : 'Boa noite'}${primeiroNome ? `, ${primeiroNome}` : ''}`;
        if (!haras && !fonte.ficticia) {
          return (
            <div className="space-y-6">
              <CabecalhoPagina titulo={saudacao} />
              <EstadoVazio
                titulo={equipe ? 'Comece pelo haras' : 'Nenhum haras liberado para você ainda'}
                acao={
                  equipe && (
                    <BotaoLink variante="principal" to="/haras/novo">
                      Cadastrar o primeiro haras
                    </BotaoLink>
                  )
                }
              >
                {equipe
                  ? 'Depois do haras vêm os animais e o planejamento da visita.'
                  : 'O plantel aparece aqui assim que a equipe Equiview liberar o seu haras.'}
              </EstadoVazio>
            </div>
          );
        }
        const plantel = animais.filter((a) => !haras || a.owner_farm_id === haras.id);
        const doPlantel = new Set(plantel.map((a) => a.id));
        const fichas = avaliacoes.filter((a) => doPlantel.has(a.animal_id));
        return (
          <div className="space-y-6">
            <CabecalhoPagina
              sobretitulo={haras?.name}
              titulo={saudacao}
              selo={
                <>
                  <span aria-hidden className="size-2 rounded-full bg-destaque" />
                  Hoje, {formatarData(hojeIso())}
                </>
              }
            />
            <ChipsDeVisoes />
            {equipe ? (
              <DaEquipe visitas={visitas} fichas={fichas} animais={animais} />
            ) : (
              <DoDono plantel={plantel} animais={animais} haras={haras} fichas={fichas} capas={capas} />
            )}
          </div>
        );
      }}
    </Aguardar>
  );
}

function DaEquipe({ visitas, fichas, animais }: { visitas: Visita[]; fichas: Avaliacao[]; animais: Animal[] }) {
  const navegar = useNavigate();
  const porEnviar = useLiveQuery(() => db.outbox.count(), [], 0);
  const emQuarentena = useLiveQuery(() => db.quarantine.count(), [], 0);
  const arquivosPorEnviar = useLiveQuery(async () => (await arquivos.pendentes()).length, [], 0);
  const daVisita = (v: Visita) => fichas.filter((a) => a.visit_id === v.id);
  // A visita da vez: a primeira em andamento, senao a proxima planejada.
  const abertas = visitas.filter((v) => !v.closed_at && situacaoDaVisita(daVisita(v), v) !== 'fechada');
  const daVez =
    abertas.find((v) => situacaoDaVisita(daVisita(v), v) === 'andamento') ??
    abertas.slice().sort((a, b) => a.visit_date.localeCompare(b.visit_date))[0];
  const proxima = daVez ? proximaFicha(daVisita(daVez), animais) : undefined;
  const fichasAbertas = fichas.filter((a) => a.started_at && !fechada(a));
  const porValidar = fichas.filter((a) => a.status === 'fechada').length;
  const validadas = fichas.filter((a) => a.status === 'validada').length;
  const reabertas = fichas.filter((a) => a.status === 'reaberta').length;
  const emAberto = fichas.filter((a) => !fechada(a)).length;

  const avisos: Aviso[] = [];
  if (emQuarentena > 0)
    avisos.push({
      tom: 'erro',
      conteudo: (
        <>
          <b>{emQuarentena} registro(s) recusado(s)</b> pelo servidor precisam de uma decisão antes de sair do haras.{' '}
          <Link to="/sincronizacao" className="font-semibold text-destaque-texto underline underline-offset-2">
            Resolver agora
          </Link>
        </>
      ),
    });
  if (porEnviar + arquivosPorEnviar > 0)
    avisos.push({
      tom: 'aviso',
      conteudo: (
        <>
          <b>
            {porEnviar} registro(s) e {arquivosPorEnviar} arquivo(s)
          </b>{' '}
          ainda não saíram deste aparelho. Sincronize com internet antes de sair do haras.
        </>
      ),
    });
  if (reabertas > 0)
    avisos.push({
      tom: 'info',
      conteudo: (
        <>
          <b>{reabertas} ficha(s) reaberta(s)</b> aguardando nova coleta ou fechamento.
        </>
      ),
    });

  const fechadasDaVez = daVez ? daVisita(daVez).filter(fechada).length : 0;
  const totalDaVez = daVez ? daVisita(daVez).length : 0;

  return (
    <>
      <FaixaIndicadores rotulo="Resumo do dia">
        <Indicador
          destaque
          rotulo="Fichas em coleta"
          valor={fichasAbertas.length}
          icone={<IconeRelogio width={17} height={17} />}
          complemento={`em ${abertas.length} visita${abertas.length === 1 ? '' : 's'} aberta${abertas.length === 1 ? '' : 's'}`}
        />
        <Indicador
          rotulo="Visitas abertas"
          valor={abertas.length}
          icone={<IconeDocumento width={17} height={17} />}
          complemento={daVez ? `a da vez: nº ${daVez.visit_number}` : 'nenhuma para hoje'}
        />
        <Indicador
          rotulo="Por validar"
          valor={porValidar}
          icone={<IconeSelo width={17} height={17} />}
          complemento="fichas fechadas, sem validação"
        />
        <Indicador
          rotulo="Por enviar"
          valor={porEnviar}
          icone={<IconeSincronia width={17} height={17} />}
          complemento={arquivosPorEnviar ? `registros · ${arquivosPorEnviar} arquivo(s)` : 'registros'}
        />
        <Indicador
          rotulo="Quarentena"
          valor={emQuarentena}
          icone={<IconeAlerta width={17} height={17} />}
          complemento={emQuarentena ? 'precisa de decisão' : 'tudo certo'}
        />
      </FaixaIndicadores>

      <PainelAvisos avisos={avisos} />

      <div className="grid gap-3.5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)] lg:items-start">
        {daVez ? (
          <Painel
            titulo={`Visita nº ${daVez.visit_number} · ${formatarData(daVez.visit_date)}`}
            acoes={
              <div className="flex gap-2">
                <BotaoLink tamanho="sm" to={`/visitas/${daVez.id}`}>
                  Ver visita
                </BotaoLink>
                {proxima && (
                  <BotaoLink tamanho="sm" variante="principal" to={`/visitas/${daVez.id}/fichas/${proxima.id}`}>
                    {situacaoDaVisita(daVisita(daVez), daVez) === 'planejada' ? 'Iniciar coleta' : 'Continuar coleta'}
                  </BotaoLink>
                )}
              </div>
            }
          >
            <div className="space-y-3">
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-apagado">Fichas fechadas</span>
                <span className="numero font-semibold">
                  {fechadasDaVez} de {totalDaVez}
                </span>
              </div>
              <div
                role="progressbar"
                aria-label="Fichas fechadas da visita"
                aria-valuemin={0}
                aria-valuemax={totalDaVez}
                aria-valuenow={fechadasDaVez}
                className="h-2 overflow-hidden rounded-full bg-trilha"
              >
                <div
                  className="h-full rounded-full bg-serie transition-[width] duration-300"
                  style={{ width: totalDaVez ? `${(fechadasDaVez / totalDaVez) * 100}%` : '0%' }}
                />
              </div>
              {proxima && (
                <p className="text-sm text-apagado">
                  Próximo animal:{' '}
                  <b className="font-semibold text-texto">
                    {animais.find((a) => a.id === proxima.animal_id)?.name ?? '—'}
                  </b>
                </p>
              )}
            </div>
          </Painel>
        ) : (
          <EstadoVazio
            titulo="Nenhuma visita aberta"
            acao={
              <BotaoLink variante="principal" to="/visitas/nova">
                Planejar visita
              </BotaoLink>
            }
          >
            Planeje a próxima visita: escolha os animais e o nível de cada um.
          </EstadoVazio>
        )}

        <CartaoGrafico titulo="Fichas por situação" sub="Todas as fichas do haras. Clique para ver na consulta.">
          <Rosca
            rotulo="Fichas por situação"
            unidade={fichas.length === 1 ? 'ficha' : 'fichas'}
            onEscolher={(s) => navegar(`/dados?situacao=${s}`)}
            fatias={[
              { chave: 'validadas', rotulo: 'Validadas', valor: validadas, passo: 1 },
              { chave: 'fechadas', rotulo: 'Fechadas', valor: porValidar, passo: 2 },
              { chave: 'abertas', rotulo: 'Em aberto', valor: emAberto, passo: 3 },
            ]}
            vazio="Nenhuma ficha neste haras ainda."
          />
        </CartaoGrafico>
      </div>

      {/* Sem barra lateral (celular e tablet), os atalhos continuam a um toque. */}
      <nav aria-label="Atalhos" className="flex flex-wrap gap-2 lg:hidden">
        <BotaoLink to="/animais">Plantel</BotaoLink>
        <BotaoLink to="/visitas">Visitas</BotaoLink>
        <BotaoLink to="/dados">Dados e relatórios</BotaoLink>
        <BotaoLink to="/comparar">Comparar</BotaoLink>
      </nav>
    </>
  );
}

function DoDono({
  plantel,
  animais,
  haras,
  fichas,
  capas,
}: {
  plantel: Animal[];
  animais: Animal[];
  haras: Haras | null;
  fichas: Avaliacao[];
  capas: Map<string, Midia>;
}) {
  const navegar = useNavigate();
  const hoje = hojeIso();
  const fechadas = fichas.filter(fechada).sort((x, y) => y.visit_date.localeCompare(x.visit_date));
  const avaliados = new Set(fechadas.map((a) => a.animal_id));
  // A mais recente de cada animal (fechadas ja vem da mais nova para a mais antiga).
  const ultimaPorAnimal = new Map<string, Avaliacao>();
  for (const f of fechadas) if (!ultimaPorAnimal.has(f.animal_id)) ultimaPorAnimal.set(f.animal_id, f);
  const recentes = [...ultimaPorAnimal.values()].slice(0, 6);
  const nome = new Map(plantel.map((a) => [a.id, a]));
  // O dono so ve o que fechou: a situacao do plantel conta as fichas fechadas.
  const r = resumirPlantel(montarLinhas(animais, fechadas, haras, hoje));

  return (
    <>
      <FaixaIndicadores rotulo="Resumo do plantel">
        <Indicador
          destaque
          rotulo="Animais avaliados"
          valor={avaliados.size}
          icone={<IconeSelo width={17} height={17} />}
          complemento={`de ${plantel.length} no plantel`}
        />
        <Indicador
          rotulo="Em dia"
          valor={r.emDia}
          icone={<IconeLista width={17} height={17} />}
          complemento="avaliados nos últimos 12 meses"
        />
        <Indicador
          rotulo="Última avaliação"
          valor={fechadas[0] ? formatarData(fechadas[0].visit_date) : '—'}
          icone={<IconeRelogio width={17} height={17} />}
        />
      </FaixaIndicadores>

      <div className="grid gap-3.5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Painel
          titulo="Avaliados recentemente"
          corpo="p-0 sm:p-4"
          acoes={
            <BotaoLink tamanho="sm" to="/animais?ordem=avaliacao&dir=desc">
              Ver o plantel
            </BotaoLink>
          }
        >
          {recentes.length === 0 ? (
            <p className="p-4 text-sm text-apagado sm:p-0">Nenhuma avaliação fechada ainda.</p>
          ) : (
            // Celular: linha compacta com miniatura (uma foto grande por animal e rolagem demais).
            // Do sm em diante, o cartao com a foto.
            <ul className="divide-y divide-borda sm:grid sm:grid-cols-2 sm:gap-4 sm:divide-y-0 xl:grid-cols-3">
              {recentes.map((a) => {
                const animal = nome.get(a.animal_id);
                if (!animal) return null;
                return (
                  <li
                    key={a.id}
                    className="flex items-center sm:block sm:overflow-hidden sm:rounded-(--radius-controle) sm:border sm:border-borda"
                  >
                    <Link
                      to={`/animais/${animal.id}?ficha=${a.id}`}
                      className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5 active:bg-destaque-fundo sm:block sm:p-0 sm:hover:bg-destaque-fundo/40"
                    >
                      <FotoDaMidia
                        midia={capas.get(animal.id)}
                        alt={animal.name}
                        className="size-14 shrink-0 rounded-(--radius-controle) sm:aspect-3/2 sm:size-auto sm:w-full sm:rounded-none"
                      />
                      <div className="min-w-0 sm:px-3 sm:py-2">
                        <p className="font-serif leading-snug font-semibold text-marca">{animal.name}</p>
                        <p className="text-xs text-apagado">
                          {idadeDoAnimal(animal, hoje)} · avaliado em {formatarData(a.visit_date)}
                        </p>
                      </div>
                    </Link>
                    <div className="shrink-0 pr-2 sm:border-t sm:border-borda sm:px-3 sm:py-2">
                      <Link
                        to={`/animais/${animal.id}/laudo?ficha=${a.id}`}
                        className="flex min-h-11 items-center px-2 text-sm font-medium text-destaque-texto hover:underline sm:inline-flex sm:min-h-0 sm:px-0"
                      >
                        <span className="sm:hidden">Laudo</span>
                        <span className="hidden sm:inline">Abrir o laudo</span>
                      </Link>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Painel>

        <CartaoGrafico
          titulo="Situação de avaliação"
          sub="Em dia: avaliado nos últimos 12 meses. Clique para ver no plantel."
        >
          <Rosca
            rotulo="Situação de avaliação do plantel"
            unidade={r.total === 1 ? 'animal' : 'animais'}
            onEscolher={(s) => navegar(`/animais?avaliacao=${s}`)}
            fatias={[
              { chave: 'em-dia', rotulo: ROTULO_SITUACAO['em-dia'], valor: r.emDia, passo: 1 },
              { chave: 'vencida', rotulo: ROTULO_SITUACAO.vencida, valor: r.vencida, passo: 2 },
              { chave: 'nunca', rotulo: ROTULO_SITUACAO.nunca, valor: r.nunca, passo: 3 },
            ]}
            vazio="Nenhum animal no plantel ainda."
          />
        </CartaoGrafico>
      </div>
    </>
  );
}
